import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import type { LiveRoomInfo } from "../../../shared/game-preview/LiveRoomPreview";
import { flagUrl } from "../../../shared/flags";
import { RANKED } from "../../../shared/rating/ranked";
import type { RankedMode } from "../../../shared/rating/store";
import { useOpenRooms, useRankedRatings, useRoyale, mmss } from "./hooks";
import { LiveWindow, LiveFoot, LiveModeChip, liveLine, RankButtons, RoomsBody, RoyaleInfo, RoyClock, RoySeats } from "./parts";
import { Crown } from "./icons";
import WhoPlays from "./WhoPlays";
import DropEmitter from "../../../shared/DropEmitter";
import { useIsTouch } from "../../../shared/useResponsive";
import s from "./start.module.scss";
import Daily from "../../../shared/progress-ui/Daily";
import { AnnounceBar } from "../../../shared/announce/Announce";

// The start page on a computer: the live Play Now window with the PLAY NOW bar
// and the Royale card on the left, Ranked and Game Rooms on the right. Every
// card leads to its own page; nothing is joined from here.
// The Royale card is the gold block with the crown and the last winner ("e").
// The two it was picked over can still be looked at: /?royale=a (the clock
// first) and /?royale=0 (the plain card). Read from the address only.
function readRoyaleLook(): string {
  try {
    const q = (new URLSearchParams(window.location.search).get("royale") || "").toLowerCase();
    return q === "a" || q === "0" ? q : "e";
  } catch { return "e"; }
}
export default function StartPC({ onLogin, onLocal }: { onLogin: () => void; onLocal: () => void }) {
  const navigate = useNavigate();
  const [info, setInfo] = useState<LiveRoomInfo | null>(null);
  const royale = useRoyale();
  const rooms = useOpenRooms();
  const { ratings, signedIn } = useRankedRatings();

  const line = liveLine(info);
  // With a keyboard, Play Now first asks who is playing (WhoPlays); the answer
  // rides to the page as router state. A touch device is one player: straight in.
  const touch = useIsTouch();
  const [asking, setAsking] = useState(false);
  const playNow = () => (touch ? navigate("/play-now") : setAsking(true));
  const openRoyale = () => navigate("/royale");
  const openRanked = (mode: RankedMode) => (signedIn ? navigate("/ranked/" + RANKED[mode].slug) : onLogin());
  const onKey = (go: () => void) => (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); }
  };
  const last = royale.count?.lastWinner;
  const [royaleLook] = useState(readRoyaleLook);

  return (
    <div className={s.pc}>
      <div className={s.backdrop} aria-hidden="true">
        <div className={s.aurora}><i className={s.blobTeal} /><i className={s.blobGreen} /><i className={s.blobViolet} /></div>
        <div className={s.grain} />
        <div className={s.vignette} />
      </div>

      <div className={s.main}>
        <div className={s.left}>
          <AnnounceBar />
          {/* PLAY NOW: the live window on top, the button under it. */}
          <div className={s.jp} data-cardslot="" role="button" data-sound="confirm" tabIndex={0} aria-label="Play Now" onClick={playNow} onKeyDown={onKey(playNow)}>
            <div className={s.window}>
              <LiveWindow onInfo={setInfo} />
              <div className={s.winTop}>
                {line ? <><span className={s.live}><i />LIVE</span><b>{line}</b><LiveModeChip info={info} /></> : null}
              </div>
              <LiveFoot info={info} />
            </div>
            {/* the shop cards' rising drops, in the button's colours: a few while the
                card is hovered, many while the button is */}
            <div className={s.jpWrap}>
              <DropEmitter hostClass={s.jpDropHost} dropClass={s.jpDrop} scale={2.4} density={5} />
              <div className={s.jpBar}>
                <span className={s.shine} />
                <span className={s.jpTxt}>
                  {/* the phones' button says "LET'S GO!" */}
                  <b>LET'S GOOO!</b>
                </span>
              </div>
            </div>
          </div>

          {/* ROYALE: the clock to the next one, and how full it is. */}
          {royaleLook === "a" ? (
            // A: the clock first, a seat for every player
            <div className={`${s.roy} ${s.royA}`} role="button" data-sound="confirm" tabIndex={0} aria-label="Royale" onClick={openRoyale} onKeyDown={onKey(openRoyale)}>
              <span className={s.royShine} />
              <RoyClock secs={royale.secs} />
              <span className={s.royText}>
                <span className={s.royName}>ROYALE</span>
                <span className={s.royTag}>Up to <b>30 players</b><i />only one survives</span>
              </span>
              <span className={s.royCrown}><Crown /></span>
              <RoySeats pct={royale.pct} />
              {last && last.name ? (
                <span className={s.royFoot}>
                  <span className={s.royLast}>Last won by {last.flag ? <img src={flagUrl(last.flag)} alt="" /> : " "}<b>{last.name}</b></span>
                </span>
              ) : null}
            </div>
          ) : royaleLook === "e" ? (
            // E: a gold block with the crown, the last winner on a dark plate under it
            <div className={`${s.roy} ${s.royE}`} data-cardslot="" role="button" data-sound="confirm" tabIndex={0} aria-label="Royale" onClick={openRoyale} onKeyDown={onKey(openRoyale)}>
              {/* one band of light across the whole card, block and all */}
              <span className={`${s.royShine} ${s.royShineAll}`} />
              <span className={s.royBlock}>
                <Crown />
                {/* the plate keeps its room before the last winner is known, so the card
                    (and the Play Now card beside it) doesn't change size as it arrives */}
                {last && last.name
                  ? <span className={s.royPlate}>{last.flag ? <img src={flagUrl(last.flag)} alt="" /> : null}<b>{last.name}</b></span>
                  : <span className={s.royPlate} style={{ visibility: "hidden" }} aria-hidden="true"><b>&nbsp;</b></span>}
              </span>
              <span className={s.royBody}>
                <span className={s.royRow}>
                  <span className={s.royName}>ROYALE</span>
                  <span className={s.royClockRow}><RoyaleInfo /><RoyClock secs={royale.secs} /></span>
                </span>
                <span className={s.royTag}>Up to <b>30 players</b><i />only one survives</span>
                <RoySeats pct={royale.pct} />
              </span>
              {/* gold drops rising from the card's bottom edge while it is hovered (the gentle set only) */}
              <DropEmitter hostClass={s.royDropHost} dropClass={s.royDrop} scale={2.4} density={5} slowOnly />
            </div>
          ) : (
            <div className={s.roy} role="button" data-sound="confirm" tabIndex={0} aria-label="Royale" onClick={openRoyale} onKeyDown={onKey(openRoyale)}>
              <span className={s.royShine} />
              <span className={s.royName}>ROYALE</span>
              <span className={s.royClock}>{mmss(royale.secs)}</span>
              <span className={s.royCrown}><Crown /></span>
              <span className={s.royTag}>Up to <b>30 players</b><i />only one survives</span>
              <span className={s.fill} aria-hidden="true"><i style={{ width: `${royale.pct}%` }} /></span>
              {last && last.name ? (
                <span className={s.royFoot}>
                  <span className={s.royLast}>Last won by {last.flag ? <img src={flagUrl(last.flag)} alt="" /> : " "}<b>{last.name}</b></span>
                </span>
              ) : null}
            </div>
          )}
          <section className={`${s.box} ${s.boxDaily}`} aria-label="Your level and today's quests">
            <Daily onLogin={onLogin} row />
          </section>
        </div>

        <aside className={s.side}>
          <section className={s.box}>
            <div className={s.boxHead}><h2>Ranked</h2>{!signedIn && <button type="button" className={s.signIn} onClick={onLogin}>Sign in to play ranked</button>}</div>
            <RankButtons ratings={ratings} signedIn={signedIn} onOpen={openRanked} />
          </section>
          <section className={`${s.box} ${s.boxGrow}`}>
            <div className={s.boxHead}><h2>Game Rooms</h2></div>
            <RoomsBody rooms={rooms} onLocal={onLocal} />
          </section>
        </aside>
        {/* the level and today's quests, as a third column on a wide window, without a box
            (narrower, they are the row under the Royale instead) */}
        <aside className={s.side3} aria-label="Your level and today's quests">
          <Daily onLogin={onLogin} column />
        </aside>
      </div>
      {asking && <WhoPlays go="Play" onCancel={() => setAsking(false)} onPick={(n) => navigate("/play-now", { state: { who: n } })} />}
    </div>
  );
}
